import {
  Fragment,
  createRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DefaultEventsMap } from '@socket.io/component-emitter';
import { useQueryClient } from '@tanstack/react-query';
import { Card, Space, Typography } from 'antd';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Socket, io } from 'socket.io-client';
import styled from 'styled-components';
import { useMediaQuery } from 'usehooks-ts';
import { ProcInstanceContext, UserProcStepContext } from '@/entities/proc';
import { ProcsComponentsRenderer } from '@/features/proc-management';
import { ProcInstanceSetOwnerBanner } from '@/pages/proc-instance/ProcInstanceSetOwnerBanner';
import { StepItemResponse, useProcInstanceDetails } from '@/shared/api';
import { useThemeContext, themeConfig } from '@/shared/theme';
import {
  ChatMode,
  ComponentMode as Mode,
  NotificationType,
  UserInstanceParams,
} from '@/shared/types';
import { Banner, Row } from '@/shared/ui';
import { captureError } from '@/shared/utils/error-reporting';
import { InstanceStepChat } from '@/widgets/chat';
import { PageLayout, PageRefProps } from '@/widgets/page-layout';
import { StepsSidebar } from '@/widgets/steps-sidebar';
import { ProcInstanceDeprecatedBanner } from './ProcInstanceDeprecatedBanner';
import { ProcInstanceNotificationsSidebar } from './ProcInstanceNotificationsSidebar';
import { ProcInstancePublicPageHeader } from './ProcInstancePublicPageHeader';

const { Title } = Typography;
const URL = import.meta.env.VITE_API_BASE_URL;
let socket: Socket<DefaultEventsMap, DefaultEventsMap>;

export const ProcInstancePublicPage = () => {
  const { theme } = useThemeContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { procId: instanceId, code } = useParams<UserInstanceParams>();
  const isMobileView = useMediaQuery('(max-width: 768px)');

  const containerRef = useRef(null);
  const pageRef = useRef<PageRefProps>(null);
  const sidebarRef = useRef(null);

  const [selectedStepId, setSelectedStepId] = useState<string>();

  const {
    data: procData,
    error,
    refetch: refetchInstance,
  } = useProcInstanceDetails(
    {
      pathParams: { id: instanceId! },
      queryParams: { code: code! },
    },
    {
      enabled: Boolean(instanceId),
      onError(error: any) {
        if (
          error.stack.statusCode === 400 ||
          error.stack.statusCode === 403 ||
          error.stack.statusCode === 404
        ) {
          navigate('/404', { replace: true });
        }
      },
    },
  );

  const areComponentsEditale =
    procData?.state === 'active' || procData?.state === 'instantiated';
  const isSuspended = procData?.state === 'suspended';
  const isCompleted = procData?.state === 'completed';
  const isDeprecated = procData?.state === 'deprecated';
  const isOwnerEmpty = procData && !procData?.owner.email;
  const shouldShowSubmitButton =
    procData?.stepData.administration.showSubmitButton && areComponentsEditale;

  const stepRefs = useMemo(
    () =>
      procData?.stepData.steps.reduce((acc, value) => {
        acc[value.id] = createRef<HTMLDivElement>();
        return acc;
      }, {}),
    [procData?.stepData.steps],
  );

  const handleStepSelect = (id: string) => {
    stepRefs?.[id as keyof typeof stepRefs].current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
    setSelectedStepId(id);
    if (isMobileView) {
      pageRef.current?.closeLeftSidebar();
    }
  };

  const onNotificationReceived = (notification: any) => {
    switch (notification.type) {
      case NotificationType.UserAddedComment:
      case NotificationType.WorkspaceMemberAddedComment:
        if (notification.instance.id === instanceId) {
          // refetch notifications for this instance
          queryClient.invalidateQueries([
            'publicNotificationsGetPaginated',
            instanceId,
          ]);
          // refetch this instance as this step's prop: hasUnseenComments should be updated
          refetchInstance();
          // refetch notifications count for this instance
          queryClient.invalidateQueries([
            'notifications',
            'instance',
            'user',
            instanceId,
            'info',
            { code },
          ]);

          if (notification.instance?.step?.id) {
            // refetch comments for this step
            queryClient.invalidateQueries([
              'publicAgentMessagesGetPaginated',
              instanceId,
              notification.instance.step.id,
            ]);
          }
        }
        break;

      default:
        captureError(new Error('Unknown socket notification type'), {
          module: 'proc_instance_on_error_unknown_socket_notification_type',
          action: 'unknown_socket_notification_type',
        });
    }
  };

  useEffect(() => {
    // listen for socket notifications events
    socket = io(URL, {
      extraHeaders: {
        'x-userproc-code': code!,
        'x-userproc-id': instanceId!,
      },
    });
    socket.on('notifications', onNotificationReceived);

    return () => {
      socket?.off('notifications', onNotificationReceived);
      socket?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (procData?.stepData.steps.length === 0) return;

    let timer: any = null;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (timer !== null) {
              clearTimeout(timer);
            }
            timer = setTimeout(() => {
              const index = entry.target.dataset.index;
              setSelectedStepId(procData?.stepData.steps[index!].id);
            }, 250);
          }
        });
      },
      { threshold: 0.5 },
    );

    const items = containerRef.current.querySelectorAll('[data-index]');
    items.forEach((item) => observer.observe(item));

    return () => {
      items.forEach((item: any) => observer.unobserve(item));
    };
  }, [procData]);

  if (error as any) {
    const errorData = error as any;
    const errorCode = errorData?.stack?.error;
    if (errorCode === 'ResourceNotFound') {
      return <Navigate to="/404" />;
    }
  }

  return (
    <ProcInstanceContext.Provider
      value={{ procInstance: procData, selectedStepId, handleStepSelect }}
    >
      <PageLayout
        ref={pageRef}
        contentStyle={{
          padding: 0,
          background: themeConfig[theme].colors.bgBase,
        }}
        headerContent={
          <ProcInstancePublicPageHeader canSubmit={shouldShowSubmitButton} />
        }
        sidebarLeftContent={
          <StepsSidebar
            ref={sidebarRef}
            steps={procData?.stepData.steps || []}
            selectedStepId={selectedStepId}
            onStepSelect={handleStepSelect}
          />
        }
        sidebarRightContent={<ProcInstanceNotificationsSidebar />}
        sidebarRightWidth={300}
        sidebarRightCollapsed={true}
        showCloseSidebarButtons
        leftSidebarOpenedTooltip="Hide Steps"
        leftSidebarClosedTooltip="Show Steps"
        rightSidebarOpenedTooltip="Hide Notifications"
        rightSidebarClosedTooltip="Show Notifications"
      >
        {isOwnerEmpty && <ProcInstanceSetOwnerBanner />}
        {isDeprecated && <ProcInstanceDeprecatedBanner />}
        {isSuspended && (
          <Banner
            type="warning"
            message="Author suspended this instance and therefore you cannot edit it anymore."
          />
        )}
        {isCompleted && (
          <Banner
            type="info"
            message="This proc has been submitted and therefore
          you can not edit it anymore."
          />
        )}
        <Title
          style={{
            textAlign: 'center',
            marginTop: procData?.state === 'active' ? 55 : 30,
          }}
        >
          {procData?.title}
        </Title>
        <div
          ref={containerRef}
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {procData?.stepData.steps.map(
            (step: StepItemResponse, index: number) => (
              <Fragment key={step.id + '-' + index}>
                {step.visibilityType === 'Visible' && (
                  <div
                    key={step.id}
                    ref={stepRefs && stepRefs[step.id as keyof typeof stepRefs]}
                    data-index={index}
                    style={{
                      padding: '20px 0',
                      width: '100%',
                      maxWidth: 689,
                      wordBreak: 'break-word',
                    }}
                  >
                    <Card
                      title={
                        <Row style={{ gap: 8 }}>
                          <StepNumber
                            style={{
                              color:
                                theme === 'light'
                                  ? themeConfig[theme].colors.text
                                  : themeConfig[theme].colors.bgBase,
                            }}
                          >
                            #{index + 1}
                          </StepNumber>{' '}
                          {step.title}
                        </Row>
                      }
                    >
                      <UserProcStepContext.Provider value={{ stepId: step.id }}>
                        <Space
                          direction="vertical"
                          size="large"
                          style={{ width: '100%' }}
                        >
                          {step.components.map((block, index) =>
                            ProcsComponentsRenderer({
                              block,
                              index,
                              mode: areComponentsEditale
                                ? Mode.Input
                                : Mode.Disabled,
                            }),
                          )}
                          {(step.commentsEnabled ||
                            procData.stepData.administration
                              .aiAssistantEnabled) && (
                            <div style={{ marginTop: 10 }}>
                              <InstanceStepChat
                                chatMode={ChatMode.User}
                                step={step}
                                label={step.commentsLabel}
                                displayNotificationDot={step.hasUnreadComments}
                                chatEnabled={
                                  !isDeprecated &&
                                  !isSuspended &&
                                  step.commentsEnabled
                                }
                                aiAssistantEnabled={
                                  !isDeprecated &&
                                  !isSuspended &&
                                  procData.stepData.administration
                                    .aiAssistantEnabled
                                }
                                postedBy="publicUser"
                              />
                            </div>
                          )}
                        </Space>
                      </UserProcStepContext.Provider>
                    </Card>
                  </div>
                )}
              </Fragment>
            ),
          )}
        </div>
      </PageLayout>
    </ProcInstanceContext.Provider>
  );
};

const StepNumber = styled.div`
  font-family: 'Inter', sans-serif !important;
  background: #ffed66;
  padding: 5px;
  border-radius: 5px;
  font-weight: 500;
  font-size: 11px;
  max-height: 19px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
